/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Content Explorer
 *| (C) Copyright IBM Corp. 2019, 2021
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

define([
	'react',
	'prop-types',
	'@ba/ba-ui-carbon-toolkit',
	'@ba/ba-ui-carbon',
	'../app/nls/StringResources',
	'../lib/@waca/core-client/js/core-client/utils/BidiUtil',
	'@ba/ba-graphics/dist/illustrations-js/no-pins_128',
	'@ba/ba-graphics/dist/icons-js/subtract--alt_32',
	'@ba/ba-graphics/dist/icons-js/edit_32',
	'@ba/ba-graphics/dist/icons-js/checkmark_32',
	'@ba/ba-graphics/dist/icons-js/close_32'

], function (React, PropTypes, UI_CARBON_TOOLKIT, UI_CARBON, StringResources, BidiUtil, noPinIcon, removeButton, renameButton, confirmButton, cancelRenameButton) {

	const { List,
		Label,
		TruncatedText,
		FlexItem,
		FlexLayout,
		Button,
		SVGIcon,
		Container } = UI_CARBON_TOOLKIT;
	const { ClickableTile, TextInput } = UI_CARBON;
	const keyNames = UI_CARBON_TOOLKIT.keyboardUtils.keyNames;

	class PinsListView extends React.Component {
		constructor(props) {
			super(props);
			this.state = {
				nameIsInvalid: false
			};
			this._disableInputBlur = false;
			this.disableRenameInputBlurEvent = this.disableRenameInputBlurEvent.bind(this);
			this.enableRenameInputBlurEvent = this.enableRenameInputBlurEvent.bind(this);
		}

		pinMatches(pinWidget) {
			return this._getSearchableFieldValue(pinWidget).toLowerCase().indexOf(this.props.searchText.toLowerCase()) !== -1;
		}

		/**
		 * Builds searchable string for provided pin
		 *
		 * @public
		 *
		 * @param  {object} value - Pin object
		 * @return {string} fields - String that contains widget info
		 */
		_getSearchableFieldValue(value) {
			// concatenate multiple values to search
			const separator = '___';
			let fields = value.displayName + separator + value.sourceName;
			if (value.contentType === 'boardFragment' && value.content.widgets) {
				// if looking at a boardFragment with widgets, use the mapping labels
				value.content.widgets.forEach(widget => {
					fields = fields + separator + this.props.smartNamingSvc.getLocalizedWidgetType(widget);
					widget.mapping && widget.mapping.forEach(mapping => {
						fields = fields + separator + mapping.label;
					});
					// check live widgets
					const items = widget.data &&
						widget.data.dataViews &&
						widget.data.dataViews[0] &&
						widget.data.dataViews[0].dataItems;
					items && items.forEach(item => {
						fields = fields + separator + item.itemLabel;
					});
				});
			}

			return fields;
		}

		onPinRenameButtonClick(event, item) {
			this.setState({
				currEditingPin: item
			});
			this._newName = item.displayName;
			event.stopPropagation();
		}

		onPinRenameButtonKeyDown(event) {
			if (event.key.toUpperCase() === keyNames.ENTER) {
				event.stopPropagation();
			}
		}

		disablePinRenameMode(event) {
			this.setState({
				currEditingPin: undefined,
				nameIsInvalid: false
			});
			delete this._newName;
			event.target.blur();
			event.stopPropagation();
		}

		onCancelRenameButtonClick(event) {
			this.disablePinRenameMode(event);
		}

		onConfirmRenameButtonClick(event) {
			if (this._hasPinNameChanged()) {
				this.props.onPinRename(this.state.currEditingPin.id, this._newName);
			}
			this.disablePinRenameMode(event);
		}

		onPinRenameInputKeyPress(event) {
			if (event.key === 'Enter') {
				if (this._hasPinNameChanged()) {
					this.props.onPinRename(this.state.currEditingPin.id, this._newName);
				}
				this.disablePinRenameMode(event);
			} else if (event.key === 'Escape') {
				this.disablePinRenameMode(event);
			}
			event.stopPropagation();
		}

		onPinNameChange(event) {
			this._newName = event.target.value;
			if (this._newName === '') {
				this.setState({ nameIsInvalid: true });
			} else {
				this.setState({ nameIsInvalid: false });
			}
		}

		disableRenameInputBlurEvent() {
			this._disableInputBlur = true;
		}

		enableRenameInputBlurEvent() {
			this._disableInputBlur = false;
		}

		_hasPinNameChanged() {
			return (this._newName && this._newName !== this.state.currEditingPin.displayName);
		}

		onPinRenameInputBlur(event) {
			if (!this._disableInputBlur) {
				if (this._hasPinNameChanged()) {
					this.props.onPinRename(this.state.currEditingPin.id, this._newName);
				}
				this.disablePinRenameMode(event);
			}
		}

		renderNameEditor() {
			return (
				<FlexItem className={'pinRenameContainer'}>
					<TextInput
						autoFocus
						onKeyDown={(event) => this.onPinRenameInputKeyPress(event)}
						onChange={(event) => this.onPinNameChange(event)}
						onBlur={(event) => this.onPinRenameInputBlur(event)}
						className={'pinRenameInput'}
						defaultValue={this.state.currEditingPin.displayName}
						placeholder={StringResources.get('renamePinTooltip')}
						invalid={this.state.nameIsInvalid}
						invalidText={StringResources.get('renamePinInvalidText')}
						size={'sm'}
					>
					</TextInput>
					<FlexItem className={'buttonContainer'} onMouseOver={this.disableRenameInputBlurEvent} onMouseOut={this.enableRenameInputBlurEvent}>
						<Button
							onClick={(event) => this.onConfirmRenameButtonClick(event)}
							tooltipPosition={'top'}
							tooltipAlignment={'center'}
							title={StringResources.get('confirmRenamePin')}
							className={'confirmNameButton'}
							disabled={this.state.nameIsInvalid}
							icon={confirmButton.default.id}
							focusable={true}
							smartNamingSvc={this.props.smartNamingSvc}
							size="small"
							variant="icon" />
						<Button
							onClick={(event) => this.onCancelRenameButtonClick(event)}
							tooltipPosition={'top'}
							tooltipAlignment={'end'}
							title={StringResources.get('cancelRenamePin')}
							className={'cancelRenameButton'}
							icon={cancelRenameButton.default.id}
							focusable={true}
							smartNamingSvc={this.props.smartNamingSvc}
							size="small"
							variant="icon" />
					</FlexItem>
				</FlexItem>
			);
		}

		renderThumbnail(pin) {
			const disableHighlight = this.state.currEditingPin && this.state.currEditingPin.id === pin.id;
			return (
				<FlexItem className={`pinThumb ${disableHighlight ? 'disableHighlight' : ''}`}>
					<img src={pin.thumbUri} />
				</FlexItem>);
		}

		renderPinInfo(pinItem) {
			return (
				<FlexItem>
					<Label className={'pinSource'} aria-label={pinItem.sourceName} label={`${pinItem.sourceName}${StringResources.get('listSeparator')} ${pinItem.pinAge}`} />
				</FlexItem>
			);
		}

		renderPinTitle(item) {
			return (
				<FlexItem className={'pinTitle'}>
					<FlexItem className={'pinNameContainer'} >
						<TruncatedText className={'pinName'} aria-label={item.displayName} value={item.displayName} />
					</FlexItem>
					<FlexItem>
						<Button
							tooltipPosition={'top'}
							tooltipAlignment={'center'}
							title={StringResources.get('renamePin')}
							className={'renamePinButton'}
							icon={renameButton.default.id}
							focusable={true}
							onClick={(event) => this.onPinRenameButtonClick(event, item)}
							onKeyDown={(event) => this.onPinRenameButtonKeyDown(event)}
							smartNamingSvc={this.props.smartNamingSvc}
							size="small"
							variant="icon" />
						<Button
							tooltipPosition={'top'}
							tooltipAlignment={'end'}
							title={StringResources.get('pinRemove')}
							className={'removePinButton'}
							icon={removeButton.default.id}
							focusable={true}
							onKeyDown={(event) => this.onKeyDownRemove(event, item.id)}
							onClick={(event) => this.props.onPinDeleted(item.id, event)}
							smartNamingSvc={this.props.smartNamingSvc}
							size="small"
							variant="icon" />
					</FlexItem>
				</FlexItem>
			);
		}

		onKeyDownRemove(event, id) {
			if (event.key.toUpperCase() === keyNames.ENTER) {
				event.preventDefault();
				event.stopPropagation();
				this.props.onPinDeleted(id, event);
			}
		}

		itemRenderer(index) {
			const item = this._renderedPins[index];
			if (this.state.currEditingPin && this.state.currEditingPin.id === item.id) {
				return (
					<ClickableTile
						className={'pinTile pinItem'}
						data-index={index} selectable="true"
						data-id={item.id}
						tabIndex={0}
					>
						<FlexLayout
							fullHeight
							direction="column"
							alignItems='flex-start'
							className='pinTileLayout'
							role={'button'}
						>
							{this.renderThumbnail(item)}
							{this.renderNameEditor(item)}
							{this.renderPinInfo(item)}
						</FlexLayout>
					</ClickableTile>
				);
			}
			return (
				<ClickableTile
					handleKeyDown={this.props.onKeyDown.bind(this, item.id)}
					handleClick={this.props.handleClick.bind(this, item.id)}
					className={'pinTile pinItem'}
					data-index={index} selectable="true"
					data-id={item.id}
					tabIndex={0}
				>
					<FlexLayout
						fullHeight
						direction="column"
						alignItems='flex-start'
						className='pinTileLayout'
						role={'button'}
					>
						{this.renderThumbnail(item)}
						{this.renderPinTitle(item)}
						{this.renderPinInfo(item)}
					</FlexLayout>
				</ClickableTile>
			);
		}

		renderPinsCount(numPins) {
			const formattedCount = BidiUtil.enforceNumericShaping(numPins);
			return (
				<Label className={'pinCount'} label={
					StringResources.get(('pinCount'), {
						smart_count: formattedCount,
						count: formattedCount
					})} />
			);
		}

		render() {
			this._renderedPins = this.props.items.filter(this.pinMatches.bind(this));
			const numPins = this._renderedPins.length;
			const style = { 'overflow-y': 'hidden' };
			if (numPins > 0) {
				return (
					<Container className={'pinsList'}>
						{this.renderPinsCount(numPins)}
						<List
							className={'list'}
							length={numPins}
							itemRenderer={this.itemRenderer.bind(this)}
							selectionMode='single'
							style={style}
						/>
					</Container>
				);
			} else {
				return (
					<FlexLayout
						className='emptyTableContent'
						direction='column'
						alignItems='center'
					>
						<SVGIcon
							iconId={noPinIcon.default.id}
							className='customVisInfoIcon'
							role='img'
							size="xxlarge"
						/>
						<Label className={'noPinsLabel'} label={StringResources.get('NoPinsCollected')} />
					</FlexLayout>
				);
			}
		}
	}

	PinsListView.propTypes = {
		items: PropTypes.array,
		onKeyDown: PropTypes.function,
		onPinDeleted: PropTypes.function,
		onPinRename: PropTypes.function,
		searchText: PropTypes.string,
		smartNamingSvc: PropTypes.object,
		handleClick: PropTypes.function
	};

	return PinsListView;
});